import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

export type MovementReason =
  | 'sale'
  | 'admin_release_transaction'
  | 'admin_restock'
  | 'admin_adjust'
  | 'admin_set_starting'
  | 'tracking_enabled'
  | 'tracking_disabled';

export interface InventoryStateRow {
  productId: string;
  name: string;
  sku: string | null;
  categoryId: string;
  categoryName: string;
  inventoryTracked: boolean;
  inventoryStarting: number | null;
  inventoryRemaining: number | null;
  soldOrAdjusted: number | null;
  /**
   * False once a sale or hold-release exists for the product, meaning the
   * starting count can no longer be rewritten. Advisory only — the server
   * enforces the same rule and answers 409 `inventory_locked`.
   */
  startingEditable: boolean;
}

export interface InventoryMovement {
  id: string;
  productId: string;
  delta: number;
  reason: MovementReason;
  transactionId: string | null;
  transactionItemId: string | null;
  adminUserId: string | null;
  note: string | null;
  createdAt: string;
}

export function useInventoryState(eventId: string) {
  return useQuery({
    queryKey: ['event', eventId, 'inventory'],
    queryFn: () =>
      apiFetch<{ rows: InventoryStateRow[] }>(
        `/api/v1/admin/events/${eventId}/reports/inventory`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId),
  });
}

export function useInventoryMovements(eventId: string, productId: string | null) {
  return useQuery({
    queryKey: ['event', eventId, 'inventory', productId, 'movements'],
    queryFn: () =>
      apiFetch<{ rows: InventoryMovement[]; nextCursor: string | null }>(
        `/api/v1/admin/events/${eventId}/reports/inventory/${productId}/movements?limit=100`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId && productId),
  });
}

export interface InventoryHoldRow {
  productId: string;
  heldQuantity: number;
  heldTransactionCount: number;
}

/**
 * Per-product count of inventory units stuck on pending/canceled/voided
 * transactions that haven't been released yet. Powers the "(N held)"
 * annotation on Sales → Products.
 */
export function useInventoryHolds(eventId: string) {
  return useQuery({
    queryKey: ['event', eventId, 'inventory-holds'],
    queryFn: () =>
      apiFetch<{ rows: InventoryHoldRow[] }>(
        `/api/v1/admin/events/${eventId}/reports/inventory-holds`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId),
  });
}

export interface HoldingTransactionRow {
  transactionId: string;
  status: 'pending' | 'canceled' | 'voided';
  tenderType: 'card_present' | 'keyed' | 'cash';
  amountCents: number;
  cardBrand: string | null;
  cardLast4: string | null;
  description: string | null;
  deviceLabel: string | null;
  createdAt: string;
  succeededAt: string | null;
  canceledAt: string | null;
  voidedAt: string | null;
  heldQuantity: number;
}

/**
 * The specific transactions holding inventory for a single product. Shown in
 * the click-through modal on Sales → Products so an operator can review and
 * release. Disabled until productId is non-null.
 */
export function useInventoryHoldingTransactions(eventId: string, productId: string | null) {
  return useQuery({
    queryKey: ['event', eventId, 'inventory-holds', productId, 'transactions'],
    queryFn: () =>
      apiFetch<{ rows: HoldingTransactionRow[] }>(
        `/api/v1/admin/events/${eventId}/reports/inventory-holds/${productId}/transactions`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId && productId),
  });
}

interface SetTrackingArgs {
  productId: string;
  inventoryTracked: boolean;
  inventoryStarting?: number;
}

export function useSetInventoryTracking(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, ...body }: SetTrackingArgs) =>
      apiFetch(`/api/v1/admin/products/${productId}/inventory`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
      qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory'] });
    },
  });
}

export function useRestockProduct(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      delta,
      note,
    }: {
      productId: string;
      delta: number;
      note?: string;
    }) =>
      apiFetch(`/api/v1/admin/products/${productId}/inventory/restock`, {
        method: 'POST',
        body: JSON.stringify({ delta, note }),
      }),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
      qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory'] });
      qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory', productId, 'movements'] });
    },
  });
}

export function useAdjustProduct(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      remaining,
      note,
    }: {
      productId: string;
      remaining: number;
      note?: string;
    }) =>
      apiFetch(`/api/v1/admin/products/${productId}/inventory/adjust`, {
        method: 'POST',
        body: JSON.stringify({ remaining, note }),
      }),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
      qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory'] });
      qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory', productId, 'movements'] });
    },
  });
}
